/**
 *
 * Renders a modal for editing Task or Item Templates within a project.
 * - Supports both Task and Sub-Task editing based on props `isTask` and `isSubTask`.
 * - Initializes by fetching existing template data (tasks or items) and populating form fields.
 * - Integrates dynamic lists for materials, labor, and subcontractors, with computed totals.
 * - Provides Save, Cancel, and Duplicate Template dialog workflows.
 *
 * Props:
 * @param {boolean} open            - Controls visibility of the modal.
 * @param {() => void} dialogClose  - Callback to close the modal.
 * @param {boolean} isSubTask       - Flag indicating if editing an Item Template (sub-task).
 * @param {string} subTaskId        - ID of the sub-task template to load/edit.
 * @param {boolean} isTask          - Flag indicating if editing a Task Template.
 * @param {string} taskId           - ID of the task template to load/edit.
 * @param {() => void} saveHandler  - Callback invoked after successful save.
 *
 * State:
 * - editMode: toggles between view and edit states.
 * - fields: form input definitions and values (e.g., taskName).
 * - taskState: controls nested dialogs (profit margin, save template, list template).
 * - deleteSubTask: state for delete confirmation of sub-tasks.
 * - units, categoryList, laborOptions: lookup data for dropdowns.
 * - modal-based states (duplicateDialog, showItemTemplateDialog, showMovedModal).
 *
 * Helpers:
 * - initLoad(): loads initial template data and lookup lists.
 * - getViewport(): observes scroll to toggle Save/Cancel button visibility.
 * - preFill(): populates form fields for task templates.
 * - loadItemFromTemplate(): maps fetched template items into sub-task array.
 * - submitHandler(): orchestrates save/update logic for both task and item templates.
 * - handleAddTask(): adds a new sub-task (item) in edit mode.
 * - Duplicate check and handling via DuplicateTemplateDialog.
 */
import React, { useEffect, useRef, useState } from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Table from 'react-bootstrap/Table';
import '../../projects/scss/task.scss'
import './edit-templates.scss'
import idx from 'idx'
import { updateSubTaskAction } from "../../../slice/add-task";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import SubTask from "./subtask";
import { numberFormat } from "../../../util/currency-formatter";
import CustomInput from "../../../components/inputs/new-input";
import SaveTemplate from "../../../components/save-template";
import ListItemTemplate from "../../../components/list-template/item";
import { materialToForm } from "./binders/material";
import { subcontractorToForm } from "./binders/subcontractor";
import { laborToForm } from "./binders/labor";
import { editItemTemplate, editTaskTemplate, getItemTemplates, getTaskTemplates, deleteTaskTemplateItem, getExistingItemTemplate } from "../../../actions/template-management.action";
import { getSubcontractorsData } from "../../projects/utils/get-subcontractors";
import { getLaborList, getUnits, listCategories } from "../../../actions/inventory.action";
import { formatTaskData } from "../../../components/save-template/formatters/task";
import { formatSubTaskData } from "../../../components/save-template/formatters/subtask";
import { addResourcesAction } from "../../../slice/resources";
import DuplicateTemplateDialog from "../../../components/duplicate-template-dialog";

export default function EditTemplate({ open, dialogClose, isSubTask = false, subTaskId = '', isTask = false, taskId = '', saveHandler, onRefreshTemplateList, ...props }) {

  const dispatch = useDispatch()

  const projectDetail = useSelector(state => state.projectDetail) || {}
  const task = useSelector(state => state.task)
  let laborOptions = useSelector(state => state.resources.active)
  const [editMode, setEditMode] = useState((props.isSubTask || props.isTask) || false);
  const [fields, setFields] = useState({
    taskName: {
      name: 'taskName',
      label: 'Task Name',
      type: 'text',
      touched: false,
      error: false,
      valid: true,
      value: '',
      validation: ['required']
    },
  })
  const [taskState, setTaskState] = useState({
    showlockIcon: true,
    profitMarginDialogState: false,
    showItemTemplateDialog: false,
    saveTemplateDialog: false,
    saveTemplateData: {
      type: '',
      data: {}
    }
  })
  const [deleteSubTask, setDeleteSubTask] = useState({
    open: false,
    id: [],
    type: 'Item',
    index: '',
    description: '',
  });
  const [units, setUnits] = useState([]);
  const categoryList = useSelector(state => state.catalog.catalogs) || [];
  const bottomElementRef = useRef(null);
  const [isBottomVisible, setIsBottomVisible] = useState(true);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    templateName: '',
    existingTemplateId: '',
    canProceed: false,
    itemsEqual: false
  });

  useEffect(() => {
    if (categoryList.length === 0) dispatch(listCategories()).catch(e => console.error(e))
    initLoad();
    getViewport();
  }, [taskId, subTaskId])

  const getViewport = () => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsBottomVisible(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    if (bottomElementRef.current) observer.observe(bottomElementRef.current);

    return () => {
      if (bottomElementRef.current) observer.unobserve(bottomElementRef.current);
    };
  }


  // Initialize data
  const initLoad = async () => {
    try {
      await getSubcontractorsData({ type: "sub-contractor", dispatch });

      if (!laborOptions.length) {
        getLevel();
      }

      dispatch(getUnits({ sortBy: "name" })).then(res => {
        setUnits(res);
      }).catch(e => console.error(e))

      if (taskId) {
        const data = await dispatch(getTaskTemplates(taskId));
        preFill(data);
        loadItemFromTemplate(data.subTasks, true, true);
      } else if (subTaskId) {
        const response = await dispatch(getItemTemplates({ id: subTaskId }));
        if (response) {
          loadItemFromTemplate([response], true, true);
        } else {
          console.warn(`No item found in itemsTemplate with ID: ${subTaskId}`);
        }
      }
    } catch (error) {
      console.error("Error in initLoad:", error);
    }
  };

  const getLevel = async () => {
    dispatch(getLaborList()).then((data) => {
      let labors = (data || []).map((labor, i) => {
        labor.sno = i + 1;
        return labor;
      })
      dispatch(addResourcesAction(labors));
    });
  }

  const preFill = (task) => {
    setFields(prev => ({
      ...prev,
      taskName: {
        ...prev.taskName,
        value: task.name
      }
    }))
  };

  const getModalHeader = () => {
    let modalHeading = "Edit Task Template";

    if (!editMode) {
      modalHeading = isSubTask ? "Item Template Details" : "Task Template Details"
    } else {
      if (isSubTask) modalHeading = "Edit Item Template";
    }
    return modalHeading;
  }

  const inputChangeHandler = (e, fieldName = "", index = "") => {
    const subTaskArray = idx(task, _ => _.task.subTaskArray) || []
    let _temp = _.cloneDeep(subTaskArray)

    let description;
    let descriptionLength;
    if (e.target.value) {
      description = e?.target?.value.trim();
      descriptionLength = description.length;
    }

    if (_temp[index]) {
      _temp[index].descriptionError = !description;
      _temp[index].descriptionLength = description && descriptionLength < 2;
    } else {
      _temp[index] = {
        descriptionError: !description,
        descriptionLength: description && descriptionLength < 2,
      };
    }

    fields[e.target.name].value = e.target.value;
    fields[e.target.name].touched = true;
    fields[e.target.name].error = false;
    fields[e.target.name].valid = true;
    setFields(prev => ({
      ...prev,
      ...fields
    }))
  };

  const handleAddTask = () => {
    const subTaskArray = idx(task, _ => _.task.subTaskArray) || []
    let _temp = _.cloneDeep(subTaskArray)
    const clonedAccordionFields = {
      labor: [],
      subcontractor: [],
      material: [],
      taskName: "",
      is_actual_materialcost: false,
      descriptionError: true,
      descriptionLength: true,
    };
    _temp.push(clonedAccordionFields);
    dispatch(updateSubTaskAction(_temp))
  };

  const submitHandler = () => {
    let req = {}
    if (taskId) {
      req = formatTaskData(idx(task, _ => _.task), fields.taskName.value, false);
      dispatch(editTaskTemplate(taskId, req)).then((res) => {
        (idx(deleteSubTask, _ => _.id) || []).map(deleteId => {
          dispatch(deleteTaskTemplateItem(deleteId)).then((res) => { }).catch(e => console.error(e))
        })
        saveHandler()
      }).catch(e => console.error(e))
    } else if (subTaskId) {
      let { subTaskArray = [] } = idx(task, _ => _.task) || {};
      if (subTaskArray.length == 1) {
        let firstSubtaskData = subTaskArray[0];
        const templateName = firstSubtaskData.description;
        const template = {
          name: templateName,
          material: firstSubtaskData.material,
          labor: firstSubtaskData.labor,
          subcontractor: firstSubtaskData.subcontractor
        }

        dispatch(getExistingItemTemplate(template))
          .then((existingTemplate) => {
            if (existingTemplate && existingTemplate.matchingTask.id && existingTemplate.matchingTask.id !== subTaskId) {
              setDuplicateDialog({
                open: true,
                templateName: templateName,
                existingTemplateId: existingTemplate.matchingTask.id,
                canProceed: existingTemplate.canProceed,
                itemsEqual: existingTemplate.itemsEqual,
              });
            } else {
              if (existingTemplate.canProceed) {
                saveItemTemplate(firstSubtaskData);
              } else {
                setDuplicateDialog({
                  open: true,
                  templateName: templateName,
                  existingTemplateId: existingTemplate.matchingTask.id,
                  canProceed: existingTemplate.canProceed,
                  itemsEqual: existingTemplate.itemsEqual,
                })
              }
            }
          })
          .catch(error => {
            console.error("Error checking existing template:", error);
            saveItemTemplate(firstSubtaskData);
          });
      }
    }
  };

  const saveItemTemplate = (templateData) => {
    const req = formatSubTaskData({ data: templateData });
    dispatch(editItemTemplate(subTaskId, req)).then((res) => {
      saveHandler();
    }).catch(e => console.error(e));
  };

  const closeDuplicateDialog = () => {
    setDuplicateDialog({ open: false, templateName: '', existingTemplateId: '', canProceed: false, itemsEqual: false });
  };

  const handleSaveTemplateDialog = (type = '', data = {}) => {
    setTaskState(prev => ({
      ...prev,
      saveTemplateDialog: !prev.saveTemplateDialog,
      saveTemplateData: {
        type: type,
        data: data
      }
    }));
  }

  const handleItemTemplateDialog = () => {
    setTaskState(prev => ({
      ...prev,
      showItemTemplateDialog: !prev.showItemTemplateDialog,
    }));
  }

  const loadItemFromTemplate = (items = [], skipClose = false, isAdd = false) => {
    const subTaskArray = idx(task, _ => _.task.subTaskArray) || []
    let _temp = isAdd ? [] : _.cloneDeep(subTaskArray)
    for (const iterator of items) {
      const { material = [], labor = [], subcontractor = [], description, selectedQty, id, templateId, templateName, templateQuantity } = iterator;

      const cloneSubTask = {
        id: isAdd ? iterator?.id : '',
        description: description,
        selectedQty: selectedQty || templateQuantity,
        templateId: templateId || id,
        templateName: templateName || description,
        templateQuantity: templateQuantity || selectedQty,
        labor: labor.map(_labor => laborToForm(_labor, { qty: iterator.selectedQty })),
        subcontractor: subcontractor.map(_sub => subcontractorToForm(_sub, { qty: iterator.selectedQty })),
        material: material.map((rawMaterial, index) => {
          const parsedMaterial = typeof rawMaterial === 'string' ? JSON.parse(rawMaterial) : { ...rawMaterial };

          const isCatalogMoved = (iterator.movedMaterials || []).some(moved => moved.id === parsedMaterial.id)
            || parsedMaterial.catalog_moved === true;

          const isHiddenFromSearch = (iterator.hiddenMaterials || []).some(hidden => hidden.id === parsedMaterial.id)
            || parsedMaterial.hide_search === true;

          const isDifferentAccountMaterials = (iterator.differentAccountMaterials || []).some(diff => diff.id === parsedMaterial.id)
            || parsedMaterial.different_account === true;

          const isDuplicateMaterial = (iterator.duplicateMaterials || []).some(duplicate => duplicate.id === parsedMaterial.id)
            || parsedMaterial.duplicateStatus === true;

          const materialStatus = parsedMaterial.status ||
            (iterator.inactiveMaterials || []).some(inactive => inactive.id === parsedMaterial.id)
            ? 'INACTIVE'
            : 'ACTIVE';

          const enrichedMaterial = {
            ...parsedMaterial,
            catalog_moved: isCatalogMoved,
            hide_search: isHiddenFromSearch,
            status: materialStatus,
            different_account: isDifferentAccountMaterials,
            duplicateStatus: isDuplicateMaterial,
          };
          return materialToForm(enrichedMaterial, { qty: iterator.selectedQty });
        }),
        movedMaterials: iterator.movedMaterials || [],
        hiddenMaterials: iterator.hiddenMaterials || [],
        inactiveMaterials: iterator.inactiveMaterials || [],
        is_actual_materialcost: false,
        descriptionError: !description ? true : false,
        descriptionLength: !description ? true : false,
        isTemplate: iterator.isTemplate || false,
        categoryId: iterator.categoryId || '',
      };
      _temp.push(cloneSubTask);
    }
    dispatch(updateSubTaskAction(_temp))
    if (!skipClose) handleItemTemplateDialog()
  };

  const deleteSubTaskHandler = ({ subtaskIndex = '', subTaskId = [], type = 'Item', currentId = '', description = '' }) => {
    setDeleteSubTask(prev => ({
      ...prev,
      open: !prev.open,
      id: subTaskId,
      type: type,
      index: subtaskIndex,
      currentId: currentId,
      description: description,
    }))
  }
  const getDeleteSubTask = () => {
    return deleteSubTask;
  }

  return (
    <>
      <Modal
        show={open}
        onHide={() => dialogClose()}
        dialogClassName="modal-90w create-modal"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="example-custom-modal-styling-title">
            {getModalHeader()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="edit-template-modal">
          <SaveTemplate
            open={taskState.saveTemplateDialog}
            dialogClose={() => handleSaveTemplateDialog()}
            data={taskState.saveTemplateData.data}
            type={taskState.saveTemplateData.type}
          />
          {taskState.showItemTemplateDialog && <ListItemTemplate
            open={taskState.showItemTemplateDialog}
            dialogClose={() => handleItemTemplateDialog()}
            addHandler={loadItemFromTemplate}
          />}
          {isTask && <div className="row mb-3">
            <div className="col-lg-4 col-md-6 mt-3">
              <CustomInput
                placeholder="Enter task name"
                label={isSubTask ? "SubTask Name" : "Task Name"}
                {...fields.taskName}
                disabled={!editMode}
                onChange={(e) => inputChangeHandler(e)}
              />
            </div>
          </div>}
          {!isBottomVisible && <div className="position-absolute d-flex gap-2 end-0">
            {editMode && <>
              <Button type="submit" className="ms-auto task-save-btn secondarybtn" onClick={submitHandler}>SAVE</Button>
              <Button type="submit" className="secondarybtn task-cancel-btn cancelbtn" onClick={dialogClose}>CANCEL</Button>
            </>}
            {!editMode && <Button type="submit" className="ms-auto me-3 secondarybtn" onClick={() => setEditMode(true)}>
              {isSubTask ? "Edit Item" : "Edit Task"}
            </Button>}
          </div>}
          <Tabs
            defaultActiveKey="items"
            id="uncontrolled-tab-example"
            className="mb-3"
          >
            <Tab eventKey="items" title="Items">
              <SubTask
                isSubTask={isSubTask}
                profitMargin={0}
                laborOptions={laborOptions}
                profitMarginType={'PERCENTAGE'}
                subcontractors={idx(projectDetail, _ => _.subcontractors) || []}
                laborList={projectDetail.labors}
                subTaskArray={idx(task, _ => _.task.subTaskArray) || []}
                isDisabled={!editMode}
                handleSaveTemplateDialog={handleSaveTemplateDialog}
                deleteSubTaskHandler={deleteSubTaskHandler}
                getDeleteSubTask={getDeleteSubTask}
                categoryList={categoryList}
                unitList={units}
                onRefreshTemplateList={onRefreshTemplateList}
              />

              {editMode && !isSubTask && <div className="col-12 justify-content-end mt-2 gap-2 d-flex btn-res">
                <Button onClick={handleAddTask} className="primarybtn add-item-btn">ADD ITEM</Button>
              </div>}
              <div className="col-12 template-table-card mt-3 table-responsive">
                <Table bordered className="table-create mb-0 tbl-fixed">
                  <thead>
                    <tr>
                      <th className="w-50"></th>
                      <th className="col-width-8-33">Material</th>
                      <th className="col-width-8-33">Contractor</th>
                      <th className="col-width-8-33">Resource</th>
                      <th className="col-width-8-33">Planned Effort</th>
                      <th className="col-width-8-33">Total</th>
                      <th className="col-width-8-33"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td></td>
                      <td>
                        <span className="tableinfo">
                          {numberFormat(idx(task, _ => _.task.total.material.sale) || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="tableinfo">
                          {numberFormat(idx(task, _ => _.task.total.subcontractor.sale) || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="tableinfo">
                          {numberFormat(idx(task, _ => _.task.total.labor.sale) || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="tableinfo">
                          {idx(task, _ => _.task.total.forecastHours) || 0} hrs
                        </span>
                      </td>
                      <td>
                        <span className="tableinfo">
                          {numberFormat(idx(task, _ => _.task.total.total) || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="empty-icon"></span>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>

          <div className="col-12 d-flex mt-3 gap-2" ref={bottomElementRef}>
            {editMode && <>
              <Button type="submit" className="ms-auto task-save-btn secondarybtn" onClick={submitHandler}>SAVE</Button>
              <Button type="submit" className="secondarybtn task-cancel-btn cancelbtn" onClick={dialogClose}>CANCEL</Button>
            </>}
            {!editMode && <Button type="submit" className="ms-auto me-3 secondarybtn" onClick={() => setEditMode(true)} data-cy="edit-item">
              {isSubTask ? "Edit Item" : "Edit Task"}
            </Button>}
          </div>
        </Modal.Body>
      </Modal>

      <DuplicateTemplateDialog
        open={duplicateDialog.open}
        templateName={duplicateDialog.templateName}
        existingTemplateId={duplicateDialog.existingTemplateId}
        dialogClose={closeDuplicateDialog}
        canProceed={duplicateDialog.canProceed}
        itemsEqual={duplicateDialog.itemsEqual}
      />
    </>
  );

}
